import React, { useEffect, useState } from "react";
import styles from "./DashProAgencies.module.css";
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Tooltip,
  Divider,
  Box,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import { useUtil } from "../../../contexts/UtilContext";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LicenseRequiredMessage from "./LicenseRequiredMessage.jsx";
import Listing from "./AllListings";

function DashProAgencies({ accessToken, userRoles, navigateToLicenses }) {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useUtil();
  const [agencies, setAgencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("agency");
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState([]);
  const [hasVerifiedBrokerLicense, setHasVerifiedBrokerLicense] =
    useState(false);

  // Menu + delete dialog state
  const [menuAgency, setMenuAgency] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const [agencyListings, setAgencyListings] = useState([]);
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [listingNewAgencyMap, setListingNewAgencyMap] = useState({});
  // NEW: global agency for all selected listings when select all is checked
  const [globalAgencyForAll, setGlobalAgencyForAll] = useState("");
  const [selectAllListings, setSelectAllListings] = useState(false);
  const [confirmDeleteWarningOpen, setConfirmDeleteWarningOpen] =
    useState(false);

  // Open warning modal first before actual delete dialog
  const openDeleteAgencyDialog = async (agency) => {
    setMenuAgency(agency);
    setConfirmDeleteWarningOpen(true);
  };

  const allSelectedHaveNewAgency =
    selectedListingIds.length > 0 &&
    agencyListings.length === selectedListingIds.length &&
    selectedListingIds.every(
      (id) => listingNewAgencyMap[id] && listingNewAgencyMap[id] !== ""
    );

  // Fetch agencies
  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        showLoader();
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/list`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (response.status === 200 && response.data?.success) {
          setAgencies(response.data.agencies || []);
          setError(null);
        } else {
          setError({
            message: response.data?.message || "Failed to fetch agencies.",
            details: response.data?.error || "No additional information.",
          });
        }
      } catch (error) {
        setError({
          message: "Error while fetching agencies.",
          details: error.response?.data?.error || "No additional information.",
        });
      } finally {
        setLoading(false);
        hideLoader();
      }
    };
    if (accessToken) fetchAgencies();
  }, [accessToken]);

  // Fetch licenses
  useEffect(() => {
    if (accessToken) fetchLicenses();
  }, [accessToken]);

  const fetchLicenses = async () => {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/professional/professional-licenses`,
        { headers: { Authorization: accessToken } }
      );
      if (response.data.success) setLicenses(response.data.plans);
    } catch (err) {
      console.error("Failed to fetch licenses", err);
    }
  };

  const hasUnverifiedLicense = licenses.some(
    (l) => l.license_type === "broker" && l.license_number && !l.is_verified
  );

  useEffect(() => {
    setHasVerifiedBrokerLicense(
      licenses.some(
        (l) => l.license_type === "broker" && l.is_verified === true
      )
    );
  }, [licenses]);

  // Fetch professionals grouped by agency
  const fetchProfessionals = async () => {
    const allEmployees = [];
    try {
      showLoader();
      for (const agency of agencies) {
        try {
          const res = await axios.get(
            `${import.meta.env.VITE_BACKEND_URL}/agency/${
              agency.id
            }/professionals`
          );
          allEmployees.push({
            agencyName: agency.agency_name,
            agencyId: agency.id,
            professionals:
              res.status === 200 && res.data.success
                ? res.data.professionals || []
                : [],
          });
        } catch (e) {
          console.error(
            `Error fetching professionals for ${agency.agency_name}:`,
            e
          );
          allEmployees.push({
            agencyName: agency.agency_name,
            agencyId: agency.id,
            professionals: [],
          });
        }
      }
      setEmployees(allEmployees);
    } catch (e) {
      console.error("Error fetching professionals:", e);
      setEmployees([]);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (viewMode === "employee" && agencies.length > 0) fetchProfessionals();
  }, [viewMode, agencies]);

  // Navigation handlers
  const handleExploreLicense = () => navigateToLicenses();
  const handleAddAgency = () => navigate(`/MyFirstLand/agency/add`);
  const handleAddEmployee = (agencyId) =>
    navigate(`/MyFirstLand/employee/add?agency_id=${agencyId}`);
  const handleEditAgency = (agencyId) =>
    navigate(`/MyFirstLand/agency/${agencyId}/edit`);

  // Open delete dialog and fetch listings
  const openDeleteDialog = async (agency) => {
    setDeleteConfirm("");
    setMenuAgency(agency);
    setDeleteOpen(true);

    try {
      showLoader();
      const queryParams = {
        agencyId: agency.id,
      };

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/listings/agencyListing`,
        { params: queryParams }
      );
      if (response.data.success) {
        setAgencyListings(response.data.listings);
        setSelectedListingIds([]);
        setSelectAllListings(false);
        setListingNewAgencyMap({});
        setGlobalAgencyForAll("");
      } else {
        setAgencyListings([]);
      }
    } catch (error) {
      console.error("Failed to fetch listings for agency:", error);
      setAgencyListings([]);
    } finally {
      hideLoader();
    }
  };

  // Delete agency with optional listing reassignment
  const doDeleteAgency = async () => {
    if (selectedListingIds.length > 0) {
      // Validate all selected listings have an agency selected
      const unassigned = selectedListingIds.some(
        (id) => !listingNewAgencyMap[id]
      );
      if (unassigned) {
        toast.error("Please assign a new agency for every selected listing.");
        return;
      }
    }

    try {
      showLoader();

      // Prepare reassignment data if needed
      const reassignData =
        selectedListingIds.length > 0
          ? selectedListingIds.map((id) => ({
              listingId: id,
              newAgencyId: listingNewAgencyMap[id],
            }))
          : [];

      // Send delete request with reassignment data in the request body
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/${
          menuAgency.id
        }`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          data: { reassignData }, // <-- send reassignment data here
        }
      );

      toast.success("Agency deleted.");
      setAgencies((prev) => prev.filter((a) => a.id !== menuAgency.id));
      setDeleteOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to delete agency.");
    } finally {
      hideLoader();
    }
  };

  // Employee multi delete handlers
  const toggleEmployeeSelection = (agencyId, employeeId) => {
    const isAlready = selectedEmployees.some(
      (i) => i.agencyId === agencyId && i.proId === employeeId
    );
    setSelectedEmployees((prev) =>
      isAlready
        ? prev.filter(
            (i) => !(i.agencyId === agencyId && i.proId === employeeId)
          )
        : [...prev, { agencyId, proId: employeeId }]
    );
  };

  const deleteSelectedEmployees = async () => {
    try {
      showLoader();
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/remove-employee`,
        {
          data: { agencyProPairs: selectedEmployees },
        }
      );
      toast.success("Employees deleted successfully!");
      setSelectedEmployees([]);
      fetchProfessionals();
    } catch (error) {
      console.error("Error deleting employees:", error);
      toast.error("Failed to delete employees. Please try again.");
    } finally {
      hideLoader();
    }
  };

  // Loading or license check UI
  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <CircularProgress />
        <Typography variant="body1" color="textSecondary" align="center">
          Loading agencies...
        </Typography>
      </div>
    );
  }

  if (!hasVerifiedBrokerLicense) {
    return (
      <LicenseRequiredMessage
        hasUnverifiedLicense={hasUnverifiedLicense}
        onVerify={handleExploreLicense}
      />
    );
  }

  return (
    <div className={styles.container}>
      <h1 style={{ marginBottom: "3rem", marginTop: "3rem" }}>
        {viewMode === "agency"
          ? "View Your Agencies"
          : "You can view your employees here"}
      </h1>

      <Grid container justifyContent="center" sx={{ marginBottom: "30px" }}>
        <div>
          <Button
            variant={viewMode === "agency" ? "contained" : "outlined"}
            onClick={() => setViewMode("agency")}
            className={viewMode === "agency" ? styles.selectedButton : ""}
            sx={{ marginRight: "10px" }}
          >
            <BusinessIcon />
            &nbsp;Agency
          </Button>
          <Button
            variant={viewMode === "employee" ? "contained" : "outlined"}
            onClick={() => setViewMode("employee")}
            className={viewMode === "employee" ? styles.selectedButton : ""}
          >
            <PeopleIcon />
            &nbsp;Employee
          </Button>
        </div>
      </Grid>

      <div className={styles.agencyContainer}>
        <div className={styles.agencyAddContainer}>
          <h1
            style={{ marginBottom: "3rem", marginTop: "1rem" }}
            className={styles.agencyAddTitle}
          >
            {viewMode === "agency"
              ? "Add your agency here!"
              : "Add your employee here!"}
          </h1>
          <div className={styles.agencyAddButtonContainer}>
            <Button
              variant="contained"
              size="large"
              className={styles.agencyAddButton}
              onClick={
                viewMode === "agency"
                  ? handleAddAgency
                  : () => handleAddEmployee("")
              }
            >
              {viewMode === "agency" ? "Add Agency" : "Add Employee"}
            </Button>
            {viewMode === "employee" && (
              <div>
                <Button
                  variant={isSelecting ? "contained" : "outlined"}
                  onClick={() => setIsSelecting(!isSelecting)}
                  sx={{ marginBottom: "10px" }}
                >
                  {isSelecting ? "Cancel Selection" : "Remove Employee"}
                </Button>
                {isSelecting && selectedEmployees.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={deleteSelectedEmployees}
                    sx={{ marginBottom: "10px", marginLeft: "10px" }}
                  >
                    Delete Selected
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {viewMode === "agency" ? (
        <Grid container spacing={2} className={styles.agencyListContainer}>
          {agencies.map((agency) => (
            <Grid item xs={12} key={agency.id}>
              <Card
                sx={{
                  bgcolor: "#f8f9fb",
                  color: "#111827",
                  borderRadius: 3,
                  boxShadow: 3,
                  p: 2,
                  transition: "box-shadow 0.3s ease",
                  "&:hover": {
                    boxShadow: 6,
                  },
                }}
              >
                <CardHeader
                  title={
                    <Typography sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                      {agency.agency_name}
                    </Typography>
                  }
                  action={null}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, fontWeight: 600, color: "#111827" }}
                      >
                        Point of Contact: {agency.poc_first_name}{" "}
                        {agency.poc_last_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          fontSize: "0.9rem",
                          color: "#4b5563",
                        }}
                      >
                        Contact: {agency.phone || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          fontSize: "0.9rem",
                          color: "#4b5563",
                        }}
                      >
                        Email: {agency.email || "-"}
                      </Typography>
                    </Box>
                    {agency.logo_url ? (
                      <Box
                        component="img"
                        src={agency.logo_url}
                        alt="Agency Logo"
                        sx={{
                          width: 300,
                          height: 150,
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                          bgcolor: "#fff",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 160,
                          height: 120,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#f5f5f5",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                          fontSize: 12,
                          color: "#777",
                        }}
                      >
                        Logo
                      </Box>
                    )}
                  </Box>
                </CardContent>
                <Divider sx={{ borderColor: "rgba(0,0,0,0.1)", my: 1 }} />
                <CardActions sx={{ justifyContent: "flex-end", gap: 1 }}>
                  <Tooltip title="Add Agent">
                    <Button
                      size="small"
                      startIcon={<GroupAddIcon />}
                      onClick={() => handleAddEmployee(agency.id)}
                      sx={{ color: "#1e40af" }}
                    >
                      Agent
                    </Button>
                  </Tooltip>

                  <Tooltip title="Edit Agency">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditAgency(agency.id)}
                      sx={{ color: "#1e40af" }}
                    >
                      Edit
                    </Button>
                  </Tooltip>

                  <Tooltip title="Delete Agency">
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => openDeleteAgencyDialog(agency)}
                      sx={{ color: "#1e40af" }}
                    >
                      Delete
                    </Button>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <div className={styles.employeeListContainer}>
          {employees.map((group) => (
            <div key={group.agencyId}>
              <Typography variant="h6">Agency: {group.agencyName}</Typography>
              {group.professionals.length > 0 ? (
                group.professionals.map((emp) => (
                  <div
                    key={`${group.agencyId}-${emp.id}`}
                    className={`${styles.employeeCard} ${
                      selectedEmployees.some(
                        (i) =>
                          i.agencyId === group.agencyId && i.proId === emp.id
                      )
                        ? styles.selected
                        : ""
                    }`}
                  >
                    {isSelecting && (
                      <Checkbox
                        checked={selectedEmployees.some(
                          (i) =>
                            i.agencyId === group.agencyId && i.proId === emp.id
                        )}
                        onChange={() =>
                          toggleEmployeeSelection(group.agencyId, emp.id)
                        }
                      />
                    )}
                    <img
                      src={emp.photo_url || "https://via.placeholder.com/150"}
                      alt={emp.first_name}
                      className={styles.profileImage}
                    />
                    <p>
                      Name: {emp.first_name} {emp.last_name}
                    </p>
                    <p>Email: {emp.email}</p>
                    <p>Role: {emp.role}</p>
                    <p>Specialization: {emp.specialization}</p>
                    <p>Languages: {emp.language}</p>
                    <p>Years of Experience: {emp.years_of_experience}</p>
                  </div>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No employees found for this agency.
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Agency</DialogTitle>

        <Typography sx={{ mb: 2 }}>
          This will remove <b>{menuAgency?.agency_name}</b>. Type the agency
          name to confirm.
        </Typography>

        <TextField
          fullWidth
          autoFocus
          value={deleteConfirm}
          onChange={(e) => setDeleteConfirm(e.target.value)}
          placeholder={menuAgency?.agency_name || ""}
          sx={{ mb: 2 }}
        />

        {agencyListings.length > 0 && (
          <>
            <Typography sx={{ mb: 2, fontWeight: "600", color: "#b91c1c" }}>
              Please select a new agency for each listing, or mark the listing
              as Freelancer, or delete the listing by clicking on the card.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAllListings}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectAllListings(checked);
                    setSelectedListingIds(
                      checked ? agencyListings.map((l) => l.id) : []
                    );
                    if (!checked) {
                      setGlobalAgencyForAll("");
                      setListingNewAgencyMap({});
                    } else {
                      // When selecting all, clear individual assignments,
                      // user must choose global assignment dropdown
                      setListingNewAgencyMap({});
                      setGlobalAgencyForAll("");
                    }
                  }}
                />
              }
              label={`Select All (${agencyListings.length} listings)`}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {/* Global Reassign Dropdown for Select All */}
        {selectAllListings && (
          <FormControl fullWidth sx={{ mb: 2, overflow: "visible" }}>
            <InputLabel id="global-reassign-label">
              Reassign All Selected Listings To
            </InputLabel>
            <Select
              labelId="global-reassign-label"
              value={globalAgencyForAll}
              label="Reassign All Selected Listings To"
              onChange={(e) => {
                const newAgencyId = e.target.value;
                setGlobalAgencyForAll(newAgencyId);

                // Update all selected listings in listingNewAgencyMap to this newAgencyId
                const newMap = {};
                selectedListingIds.forEach((id) => {
                  newMap[id] = newAgencyId;
                });
                setListingNewAgencyMap(newMap);
              }}
            >
              <MenuItem value="-1">Freelancer</MenuItem>
              {agencies
                .filter((a) => a.id !== menuAgency?.id)
                .map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>
                    {agency.agency_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {/* Listings List with clickable cards and individual dropdown */}
        <Box
          sx={{
            maxHeight: 800,
            overflowY: "auto",
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {agencyListings.length === 0 && (
            <Typography variant="body2" color="textSecondary" align="center">
              No listings found for this agency.
            </Typography>
          )}

          {agencyListings.map((listing) => {
            const isSelected = selectedListingIds.includes(listing.id);
            const newAgencyForListing = listingNewAgencyMap[listing.id] || "";

            return (
              <Card
                key={listing.id}
                variant="outlined"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  p: 1,
                  borderColor: isSelected ? "primary.main" : "divider",
                  bgcolor: isSelected ? "action.selected" : "background.paper",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 3,
                  },
                  overflow: "visible",
                }}
                onClick={() => {
                  if (isSelected) {
                    setSelectedListingIds((prev) =>
                      prev.filter((id) => id !== listing.id)
                    );
                    setListingNewAgencyMap((prev) => {
                      const copy = { ...prev };
                      delete copy[listing.id];
                      return copy;
                    });
                  } else {
                    setSelectedListingIds((prev) => [...prev, listing.id]);
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      const checked = e.target.checked;
                      setSelectedListingIds((prev) => {
                        if (checked) {
                          if (prev.includes(listing.id)) return prev;
                          return [...prev, listing.id];
                        } else {
                          return prev.filter((id) => id !== listing.id);
                        }
                      });
                      if (!checked) {
                        setListingNewAgencyMap((prev) => {
                          const copy = { ...prev };
                          delete copy[listing.id];
                          return copy;
                        });
                      }
                    }}
                    sx={{ ml: 1, mr: 2 }}
                  />

                  {/* Listing content */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Listing listing={listing}></Listing>
                  </Box>
                </Box>

                {/* Agency dropdown only visible if listing is selected */}
                {isSelected && (
                  <FormControl fullWidth sx={{ mt: 1, overflow: "visible" }}>
                    <InputLabel id={`select-agency-${listing.id}`}>
                      Reassign to Agency
                    </InputLabel>
                    <Select
                      labelId={`select-agency-${listing.id}`}
                      value={newAgencyForListing}
                      label="Reassign to Agency"
                      onChange={(e) => {
                        const newAgencyId = e.target.value;
                        setListingNewAgencyMap((prev) => ({
                          ...prev,
                          [listing.id]: newAgencyId,
                        }));
                        if (globalAgencyForAll) {
                          setGlobalAgencyForAll("");
                        }
                      }}
                      disabled={selectAllListings}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <MenuItem value="-1">Freelancer</MenuItem>
                      {agencies
                        .filter((a) => a.id !== menuAgency?.id)
                        .map((agency) => (
                          <MenuItem key={agency.id} value={agency.id}>
                            {agency.agency_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Card>
            );
          })}
        </Box>

        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              deleteConfirm !== menuAgency?.agency_name || // Disable if name doesn't match
              (agencyListings.length > 0 && !allSelectedHaveNewAgency) // Disable only if listings exist and not all are assigned
            }
            onClick={doDeleteAgency}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledge irreversible delete modal */}
      <Dialog
        open={confirmDeleteWarningOpen}
        onClose={() => setConfirmDeleteWarningOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete Action</DialogTitle>
        <Typography sx={{ p: 3 }}>
          Deleting this agency <b>{menuAgency?.agency_name}</b> is{" "}
          <b>irreversible</b>. All associated data will be permanently removed
          or reassigned. Are you sure you want to proceed?
        </Typography>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteWarningOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmDeleteWarningOpen(false);
              openDeleteDialog(menuAgency); // open the actual delete dialog
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DashProAgencies;
